import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import './ChartLayout.css';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

interface PopulationGrowthProps {
  location: string;
}

const PopulationGrowth: React.FC<PopulationGrowthProps> = ({ location }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Track whether the component has unmounted and cap retry attempts
    let isCancelled = false;
    let retryAttempts = 0;
    const maxRetries = 50;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('PopulationGrowth: Fetching data for location:', location);

        // Get county for the location
        const county = await supabaseLocationService.getCountyForCity(location);
        console.log('PopulationGrowth: County for', location, ':', county);
        
        // Fetch population data for the selected location (all available years 1990-2045)
        const { data: locationData, error: locationError } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('location_name', location)
          .gte('year', 1990)
          .lte('year', 2045)
          .order('year', { ascending: true });

        console.log('PopulationGrowth: Location data query result:', { locationData, locationError });

        if (locationError) {
          console.error('PopulationGrowth: Location data error:', locationError);
          throw locationError;
        }

        // No county data needed - only show selected location

        if (!locationData || locationData.length === 0) {
          console.error('PopulationGrowth: No population data found for location:', location);
          throw new Error('No population data found for the selected location');
        }

        console.log('PopulationGrowth: Found', locationData.length, 'data points for', location);

        // Store location data in state for debug display
        // Prepare chart data
        const years = locationData.map(d => d.year.toString());

        // Determine the cutoff year for historical vs projected data (2024+ is projected)
        const projectionStartYear = 2024;

        // Create chart after ensuring canvas is ready
        const createChart = () => {
          if (isCancelled) {
            return;
          }

          if (!chartRef.current) {
            if (retryAttempts >= maxRetries) {
              console.warn('PopulationGrowth: Canvas not ready after maximum retries, giving up.');
              return;
            }
            retryAttempts += 1;
            console.log('PopulationGrowth: Canvas still not ready, retrying...', retryAttempts);
            setTimeout(() => {
              if (!isCancelled) {
                createChart();
              }
            }, 100);
            return;
          }

          const ctx = chartRef.current.getContext('2d');
          if (!ctx) {
            if (retryAttempts >= maxRetries) {
              console.warn('PopulationGrowth: Canvas context not available after maximum retries, giving up.');
              return;
            }
            retryAttempts += 1;
            console.log('PopulationGrowth: Canvas context not available, retrying...', retryAttempts);
            setTimeout(() => {
              if (!isCancelled) {
                createChart();
              }
            }, 100);
            return;
          }

          console.log('PopulationGrowth: Canvas ready, creating chart...');

          // Destroy existing chart if it exists
          if (chartInstance.current) {
            chartInstance.current.destroy();
          }

          // Create datasets with different styles for historical vs projected data
          const datasets = [];

          // Split data into historical (1990-2023) and projected (2024-2045)
          const historicalData = locationData.filter(d => d.year <= 2023);
          const projectedData = locationData.filter(d => d.year >= 2024);
          
          // Historical data - thick solid line
          if (historicalData.length > 0) {
            // Create array with nulls for projected years
            const historicalFullData = years.map(year => {
              const yearNum = parseInt(year);
              const dataPoint = historicalData.find(d => d.year === yearNum);
              return dataPoint ? dataPoint.population : null;
            });
            
            datasets.push({
              label: `${location} (Historical)`,
              data: historicalFullData,
              borderColor: 'rgba(59, 130, 246, 1)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: false,
              tension: 0.1,
              pointRadius: 3,
              pointHoverRadius: 5,
              spanGaps: false,
              borderWidth: 3, // Thick line for historical data
              borderDash: [] // Solid line
            });
          }

          // Projected data - dotted line
          if (projectedData.length > 0) {
            // Create array with nulls for historical years
            const projectedFullData = years.map(year => {
              const yearNum = parseInt(year);
              const dataPoint = projectedData.find(d => d.year === yearNum);
              return dataPoint ? dataPoint.population : null;
            });
            
            datasets.push({
              label: `${location} (Projected)`,
              data: projectedFullData,
              borderColor: 'rgba(59, 130, 246, 0.7)',
              backgroundColor: 'rgba(59, 130, 246, 0.05)',
              fill: false,
              tension: 0.1,
              pointRadius: 2,
              pointHoverRadius: 4,
              spanGaps: false,
              borderWidth: 2, // Thinner line for projected data
              borderDash: [5, 5] // Dotted line
            });
          }

          const chartData = {
            labels: years,
            datasets
          };

          const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            animation: false as const,
            plugins: {
              title: standardTitle(`Population Growth - ${location}`),
              legend: {
                ...standardLegend
              },
              tooltip: {
                callbacks: {
                  label: (context: any) => {
                    const value = context.parsed.y;
                    const year = context.label;
                    const isProjection = parseInt(year) >= projectionStartYear;
                    const dataSource = isProjection ? 'California Department of Finance (County Level)' : 'ACS';
                    return `${context.dataset.label}: ${value.toLocaleString()} (${dataSource})`;
                  }
                }
              }
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Year',
                  font: {
                    weight: 'bold' as const
                  }
                },
                ticks: {
                  maxRotation: 45,
                  minRotation: 45,
                  padding: 10
                },
                grid: {
                  display: false
                }
              },
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Population',
                  font: {
                    weight: 'bold' as const
                  }
                },
                grid: {
                  color: 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                  callback: (value: any) => {
                    return value.toLocaleString();
                  }
                }
              }
            }
          };

          chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: chartData,
            options: chartOptions
          });

          // Prevent chart from resizing continuously
          if (chartInstance.current.resize) {
            chartInstance.current.resize = () => {};
          }

          console.log('PopulationGrowth: Chart instance created:', chartInstance.current);
          console.log('PopulationGrowth: Chart data:', chartData);
          console.log('PopulationGrowth: Chart options:', chartOptions);
        };

        console.log('PopulationGrowth: Chart creation process started');
        createChart();
        setLoading(false);
      } catch (err) {
        console.error('PopulationGrowth: Error fetching population data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load population data');
        setLoading(false);
      }
    };

    if (location) {
      fetchData();
    }

    return () => {
      isCancelled = true;
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location]);

  if (loading) {
    return (
      <div className="chart-container" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading population data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'red' }}>Error: {error}</div>
      </div>
    );
  }

  const chartContent = (
    <div id="population-growth-chart" style={{ 
      height: '500px', 
      width: '100%', 
      position: 'relative'
    }}>
      <canvas 
        ref={chartRef} 
        style={{ 
          height: '500px', 
          width: '100%',
          display: 'block'
        }} 
      />
    </div>
  );

  return chartContent;
};

export default PopulationGrowth;
